import { useState, useCallback } from 'react';
import { supabase } from '@/lib/customSupabaseClient';

const PLAN_LABELS = {
    basic: 'Básico (10.000)',
    master: 'Master (20.000)',
    premium: 'Premium (50.000)',
    // Legados para retrocompatibilidade
    intermediate: 'Master (20.000)',
    advanced: 'Premium (50.000)',
    custom: 'Personalizado',
};

const PLAN_SHORT = {
    basic: 'Básico',
    master: 'Master',
    premium: 'Premium',
    intermediate: 'Master',
    advanced: 'Premium',
    custom: 'Personalizado',
};

export const useSalesQuota = (activeAccountId) => {
    const [quota, setQuota] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetchQuota = useCallback(async () => {
        if (!activeAccountId) return null;
        setLoading(true);
        try {
            const { data, error } = await supabase.rpc('get_user_sales_quota', { p_user_id: activeAccountId });
            if (error) throw error;
            setQuota(data);
            return data;
        } catch (err) {
            console.error('Erro ao buscar cota de vendas:', err);
            return null;
        } finally {
            setLoading(false);
        }
    }, [activeAccountId]);

    /**
     * Verifica se é possível gravar uma quantidade de vendas.
     * Retorna { allowed: true } ou { allowed: false, message, current, limit, trying }
     */
    const checkQuota = useCallback(async (quantityToAdd = 0) => {
        if (!activeAccountId) return { allowed: true };

        const { data, error } = await supabase.rpc('get_user_sales_quota', { p_user_id: activeAccountId });
        if (error) {
            console.error('Erro ao verificar cota:', error);
            // Em caso de erro na verificação, permitir (fail-open) para não bloquear o usuário
            return { allowed: true };
        }

        // Se não tem limite definido, permitir
        if (data.sales_limit === null || data.sales_limit === undefined) {
            return { allowed: true };
        }

        const currentCount = data.sales_count || 0;
        const limit = data.sales_limit;
        const planName = PLAN_LABELS[data.sales_plan] || data.sales_plan || 'Atual';

        // Já atingiu o limite (sem tentar adicionar novas)
        if (currentCount >= limit) {
            return {
                allowed: false,
                message: `Limite do plano ${planName} atingido! Você possui ${currentCount.toLocaleString('pt-BR')} de ${limit.toLocaleString('pt-BR')} vendas permitidas (0 disponíveis). Exclua vendas antigas ou contrate um plano maior.`,
                current: currentCount,
                limit,
                trying: quantityToAdd,
                remaining: 0,
            };
        }

        // Tentando adicionar mais do que o espaço disponível
        if (currentCount + quantityToAdd > limit) {
            const remaining = limit - currentCount;
            return {
                allowed: false,
                message: `Espaço insuficiente! Você está tentando importar ${quantityToAdd.toLocaleString('pt-BR')} vendas, mas só tem ${remaining.toLocaleString('pt-BR')} vaga(s) disponível(is) no plano ${planName} (${currentCount.toLocaleString('pt-BR')}/${limit.toLocaleString('pt-BR')}).`,
                current: currentCount,
                limit,
                trying: quantityToAdd,
                remaining,
            };
        }

        return { allowed: true, current: currentCount, limit, remaining: limit - currentCount };
    }, [activeAccountId]);

    // Dados derivados para exibição no dashboard
    const percentage = quota && quota.sales_limit ? Math.round((quota.sales_count / quota.sales_limit) * 100) : 0;
    const isWarning = percentage >= 80 && percentage < 100;
    const isBlocked = quota && quota.sales_limit !== null && quota.sales_count >= quota.sales_limit;
    const planName = quota?.sales_plan ? (PLAN_LABELS[quota.sales_plan] || quota.sales_plan) : null;
    const planNameShort = quota?.sales_plan ? (PLAN_SHORT[quota.sales_plan] || quota.sales_plan) : (quota?.sales_limit ? 'Personalizado' : 'Sem Plano');

    return {
        quota,
        loading,
        fetchQuota,
        checkQuota,
        percentage,
        isWarning,
        isBlocked,
        planName,
        planNameShort,
    };
};
